// packages/main/app/web/components-v2/UnifiedImageDialog/index.tsx
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { TabContent, TabList, TabRoot, TabTrigger } from '../../components/Tabs';
import { Dialog, DialogBody, DialogPopup } from '../Dialog';
import { AIEditTab } from './AIEditTab';
import { AIGenerateTab } from './AIGenerateTab';
import { FreeImagesTab } from './FreeImagesTab';
import { UploadTab } from './UploadTab';
import { UploadVideoTab } from './UploadVideoTab';
import { s } from './styles';
import { TabValue, UnifiedImageDialogProps } from './types';

export function UnifiedImageDialog({
	open,
	onOpenChange,
	initialTab = 'upload',
	initialImage,
	onImageSelected,
	onVideoJobGenerated,
	onVideoAttachmentGenerated,
	impersonationContext,
	postContent,
	showUploadTab = true,
	showUploadVideoTab = false,
	disabledTabs = [],
	disabledTabsReason = {},
	acceptedFormats,
}: UnifiedImageDialogProps) {
	const [selectedTab, setSelectedTab] = React.useState<TabValue>(initialTab);
	const [aiEditImage, setAiEditImage] = React.useState<Api.IFileAttachment | null>(null);

	// Update selected tab when initialTab changes
	React.useEffect(() => {
		if (open) {
			setSelectedTab(initialTab);
			setAiEditImage(null); // Reset AI edit image when dialog opens
		}
	}, [open, initialTab]);

	const handleImageSelected = React.useCallback(
		(image: Api.IFileAttachment) => {
			onImageSelected(image);
			onOpenChange(false);
		},
		[onImageSelected, onOpenChange]
	);

	const handleEditWithAI = React.useCallback((image: Api.IFileAttachment) => {
		setAiEditImage(image);
		setSelectedTab('ai-edit');
	}, []);

	// Use aiEditImage if available, otherwise fall back to initialImage
	const editImage = aiEditImage || initialImage;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[s.dialogContent]} showCloseButton>
				<DialogBody styles={[s.dialogBody]}>
					<TabRoot
						className={css(s.tabsContainer)}
						value={selectedTab}
						onValueChange={value => setSelectedTab(value as TabValue)}
					>
						<TabList styles={[s.verticalTabList]}>
							{showUploadTab ? (
								<TabTrigger
									disabled={disabledTabs.includes('upload')}
									title={disabledTabsReason['upload']}
									value='upload'
								>
									Upload Image
								</TabTrigger>
							) : null}
							<TabTrigger
								disabled={disabledTabs.includes('ai-generate')}
								title={disabledTabsReason['ai-generate']}
								value='ai-generate'
							>
								Generate with AI
							</TabTrigger>
							{editImage && (
								<TabTrigger
									disabled={disabledTabs.includes('ai-edit')}
									title={disabledTabsReason['ai-edit']}
									value='ai-edit'
								>
									Edit with AI
								</TabTrigger>
							)}
							<TabTrigger
								disabled={disabledTabs.includes('free-images')}
								title={disabledTabsReason['free-images']}
								value='free-images'
							>
								Browse Free Images
							</TabTrigger>
							{showUploadVideoTab ? (
								<TabTrigger
									disabled={disabledTabs.includes('upload-video')}
									title={disabledTabsReason['upload-video']}
									value='upload-video'
								>
									Upload Video
								</TabTrigger>
							) : null}
						</TabList>
						<div className={css(s.tabContentWrapper)}>
							<TabContent value='upload' className={css(s.tabContent)}>
								<UploadTab
									onImageSelected={handleImageSelected}
									onEditWithAI={handleEditWithAI}
									impersonationContext={impersonationContext}
									acceptedFormats={acceptedFormats}
								/>
							</TabContent>
							<TabContent value='ai-generate' className={css(s.tabContent)}>
								<AIGenerateTab onImageSelected={handleImageSelected} impersonationContext={impersonationContext} />
							</TabContent>
							{editImage && (
								<TabContent value='ai-edit' className={css(s.tabContent)}>
									<AIEditTab
										initialImage={editImage}
										onImageSelected={handleImageSelected}
										impersonationContext={impersonationContext}
									/>
								</TabContent>
							)}
							<TabContent value='free-images' className={css(s.tabContent)}>
								<FreeImagesTab
									onImageSelected={handleImageSelected}
									onEditWithAI={handleEditWithAI}
									content={postContent}
								/>
							</TabContent>
							{showUploadVideoTab && (
								<TabContent value='upload-video' className={css(s.tabContent)}>
									<UploadVideoTab
										onVideoJobGenerated={onVideoJobGenerated}
										onVideoAttachmentGenerated={onVideoAttachmentGenerated}
										impersonationContext={impersonationContext}
									/>
								</TabContent>
							)}
						</div>
					</TabRoot>
				</DialogBody>
			</DialogPopup>
		</Dialog>
	);
}
